import { useState, useEffect, useCallback } from "react"
import { ArrowLeft, Check, Clock, Search, SortAsc } from "lucide-react"
import { Link, useNavigate } from "react-router-dom";
import cn from "classnames";
import { getAllCustomForms } from "./api"; // Adjust path if needed

export default function EditFormSelection() {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedForm, setSelectedForm] = useState(null)
  const [sortBy, setSortBy] = useState("recent")
  const [forms, setForms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showSort, setShowSort] = useState(false);
  const navigate = useNavigate();

  const fetchForms = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getAllCustomForms()
      if (response && response.error) {
        setError(response.error || "Failed to load forms.")
        setForms([])
      } else {
        console.log("Fetched forms:", response.data)
        setForms(response.data)
      }
    } catch (err) {
      setError("Failed to load forms.")
      setForms([])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchForms()
  }, [fetchForms])

  // Filter forms based on search query
  const filteredForms = forms.filter(
    (form) =>
      form.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      form.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  // Sort forms based on sort option
  const sortedForms = [...filteredForms].sort((a, b) => {
    if (sortBy === "name") {
      return a.title.localeCompare(b.title)
    } else {
      // Sort by recency if available, otherwise fallback to original order
      return 0 // Replace with your own recency logic if you have timestamps
    }
  })

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <main className="flex flex-1 flex-col p-4 md:p-8">
        <div className="mx-auto w-full max-w-4xl">
          <div className="mb-6 flex items-center">
            <Link to="/custom-form">
              <button variant="ghost" size="sm" className="mr-4 flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </button>
            </Link>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Select a Form to Edit</h1>
          </div>
          <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search forms..."
                className="pl-9 pr-8 py-2 w-full rounded border border-slate-300 focus:border-blue-400 focus:outline-none transition"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search forms"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {/* Custom sort dropdown */}
            <div className="relative">
              <button
                type="button"
                className="flex items-center border border-slate-300 rounded px-3 py-2 text-sm bg-white hover:bg-slate-100 transition"
                onClick={() => setShowSort((v) => !v)}
              >
                <SortAsc className="mr-2 h-4 w-4" />
                Sort by: {sortBy === "recent" ? "Most Recent" : "Name"}
              </button>
              {showSort && (
                <div className="absolute right-0 mt-2 w-40 rounded border border-slate-200 bg-white shadow z-10">
                  <button
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-100 ${sortBy === "recent" ? "font-semibold" : ""}`}
                    onClick={() => { setSortBy("recent"); setShowSort(false); }}
                  >
                    Most Recent
                  </button>
                  <button
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-100 ${sortBy === "name" ? "font-semibold" : ""}`}
                    onClick={() => { setSortBy("name"); setShowSort(false); }}
                  >
                    Name
                  </button>
                </div>
              )}
            </div>
          </div>
          {loading ? (
            <div className="flex h-40 items-center justify-center">
              <p className="text-slate-600">Loading forms...</p>
            </div>
          ) : error ? (
            <div className="flex h-40 items-center justify-center">
              <p className="text-red-600">{error}</p>
              <button variant="link" className="ml-2 flex items-center gap-1" onClick={fetchForms}>
                <span>Retry</span>
                {/* Optionally add an icon here if you want */}
              </button>
            </div>
          ) : sortedForms.length > 0 ? (
            <div className="space-y-4">
              {sortedForms.map((form) => (
                <div
                  key={form._id}
                  className={cn(
                    "flex cursor-pointer items-start rounded-lg border bg-white p-4 transition-all hover:border-blue-200 hover:bg-blue-50",
                    selectedForm === form._id ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : "border-slate-200",
                  )}
                  onClick={() => setSelectedForm(form._id)}
                >
                  <div className="mr-4 mt-1 flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white">
                    {selectedForm === form._id && <Check className="h-3 w-3 text-blue-600" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col justify-between sm:flex-row sm:items-center">
                      <h3 className="font-medium text-slate-900">{form.title}</h3>
                      <div className="mt-1 flex items-center text-xs text-slate-500 sm:mt-0">
                        <Clock className="mr-1 h-3 w-3" />
                        Last edited {form.lastEdited || "-"}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{form.description}</p>
                    <div className="mt-2 text-xs text-slate-500">{form.fields?.length} fields</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-40 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <p className="text-slate-600">No forms found matching your search.</p>
              <button variant="link" className="mt-2 flex items-center gap-1" onClick={() => setSearchQuery("")}>
                {/* Optionally add an icon here if you want */}
                <span>Clear search</span>
              </button>
            </div>
          )}
          <div className="mt-6 flex justify-end">
            <button
              disabled={!selectedForm}
              className={`px-6 py-2 rounded text-white font-semibold transition ${
                selectedForm
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-blue-300 cursor-not-allowed"
              }`}
              onClick={() => {
                if (selectedForm) {
                  navigate(`/custom-form/edit/${selectedForm}`);
                }
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
